import Constants from "expo-constants";
import { AppState, Platform } from "react-native";

import { usersApi } from "@/api";
import type { WebPushSubscriptionPayload } from "@/api";
import { ApiError, getAccessToken } from "@/api/client";
import i18n from "@/i18n";
import { cueIdOf, notificationExpired } from "@/lib/notification-freshness";
import {
  PushRegistrationRejectedError,
  type PushSetupOutcome,
} from "@/lib/push-setup-outcome";
import type { NotificationPreferences } from "@/types";

export type NotificationPermission = "granted" | "denied";

// Permission and delivery registration are separate facts; the decision that
// keeps them apart lives in its own dependency-free module so it can be tested
// without a browser. See `push-setup-outcome.ts` for why.
export type { PushSetupOutcome } from "@/lib/push-setup-outcome";

export type NotificationPermissionState = {
  status: NotificationPermission;
  /**
   * Whether the OS will still show its prompt. False once the user has refused
   * for good (iOS always, Android after the second refusal) — from then on only
   * the app's OS settings page can change the answer, so a caller should send
   * the user there rather than calling a request that silently does nothing.
   */
  canAskAgain: boolean;
};

/** Preference key that gates a notification (matches NotificationPreferences). */
export type NotificationCategory = Exclude<
  keyof NotificationPreferences,
  "muteAll"
>;

type NotificationsModule = typeof import("expo-notifications");
type LocalNotificationPayload = {
  body?: string;
  data?: Record<string, unknown>;
};

type LocalNotificationInput = {
  body?: string;
  data?: Record<string, unknown>;
  category?: NotificationCategory;
};

let notificationsModulePromise: Promise<NotificationsModule | null> | null =
  null;
let notificationHandlerConfigured = false;
let androidDefaultChannelReady = false;
let nativePushRegistrationInFlight:
  | { requestPermission: boolean; promise: Promise<PushSetupOutcome> }
  | null = null;
let lastUploadedNativeTokenKey: string | null = null;

type RegisterPushOptions = {
  /**
   * Whether this call may show the OS notification permission prompt.
   * Startup/resume syncs should keep this false; explicit user actions
   * (onboarding/profile toggles) can use the default true.
   */
  requestPermission?: boolean;
};

/**
 * True only in the real Expo Go client.
 *
 * We rely solely on `appOwnership === 'expo'`. We deliberately do NOT use
 * `expoGoConfig != null` (it reports truthy outside Expo Go — e.g. on web, where
 * executionEnvironment is "bare" and appOwnership is null) nor
 * `executionEnvironment === 'storeClient'` (also matches a dev-client build).
 * `appOwnership` is deprecated but is the only signal that is `null` everywhere
 * except Expo Go.
 */
function isRunningInExpoGo(): boolean {
  return Constants.appOwnership === "expo";
}

let warnedAndroidExpoGo = false;

function isAndroidExpoGo(): boolean {
  const inExpoGo = Platform.OS === "android" && isRunningInExpoGo();
  if (inExpoGo && !warnedAndroidExpoGo) {
    warnedAndroidExpoGo = true;
    console.warn(
      `[push] Detected Expo Go (appOwnership=${Constants.appOwnership}, ` +
        `expoGoConfig=${Constants.expoGoConfig != null}). Android remote push ` +
        "is unavailable in Expo Go (SDK 53+) — use a development/standalone build.",
    );
  }
  return inExpoGo;
}

async function getNotificationsModule(): Promise<NotificationsModule | null> {
  if (isAndroidExpoGo()) return null;

  notificationsModulePromise ??= import("expo-notifications")
    .then((Notifications) => {
      configureNotificationHandler(Notifications);
      return Notifications;
    })
    .catch(() => null);

  return notificationsModulePromise;
}

async function requestBrowserNotificationPermission(): Promise<NotificationPermission> {
  if (
    Platform.OS !== "web" ||
    typeof window === "undefined" ||
    !("Notification" in window)
  ) {
    return "denied";
  }

  if (globalThis.Notification.permission === "granted") {
    return "granted";
  }

  if (globalThis.Notification.permission === "denied") {
    return "denied";
  }

  const status = await globalThis.Notification.requestPermission();
  return status === "granted" ? "granted" : "denied";
}

function urlBase64ToUint8Array(value: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = `${value}${padding}`.replace(/-/g, "+").replace(/_/g, "/");
  const raw = globalThis.atob(base64);
  const output = new Uint8Array(new ArrayBuffer(raw.length));

  for (let index = 0; index < raw.length; index += 1) {
    output[index] = raw.charCodeAt(index);
  }

  return output;
}

function isWebPushSubscriptionPayload(
  value: PushSubscriptionJSON,
): value is WebPushSubscriptionPayload {
  return Boolean(value.endpoint && value.keys?.p256dh && value.keys?.auth);
}

/**
 * Whether an existing browser subscription was created with `expectedKey`.
 * A mismatch (server VAPID keys changed) means the subscription can no longer
 * receive pushes and must be replaced.
 */
function subscriptionMatchesKey(
  subscription: PushSubscription,
  expectedKey: Uint8Array,
): boolean {
  const applied = subscription.options?.applicationServerKey;
  if (!applied) return false;

  const current = new Uint8Array(applied as ArrayBuffer);
  if (current.length !== expectedKey.length) return false;

  for (let index = 0; index < current.length; index += 1) {
    if (current[index] !== expectedKey[index]) return false;
  }
  return true;
}

/**
 * Returns whether a destination was actually stored.
 *
 * Every early return here is a real "nothing was saved" — an unsupported
 * runtime, a session not ready, a missing key, an API build without the route.
 * They used to be indistinguishable from success because this returned void,
 * which is how a granted permission with no subscription reached the UI as
 * proof of delivery.
 */
async function registerBrowserPushSubscription(): Promise<
  "stored" | "unavailable"
> {
  if (
    typeof navigator === "undefined" ||
    !("serviceWorker" in navigator) ||
    !("PushManager" in window)
  ) {
    return "unavailable";
  }

  // Saving the subscription is an authenticated call. If the access token has
  // not been synced into the API client yet (registration fired before/while
  // the session hydrated), skip now instead of 401ing. The auth-keyed effect
  // in the tabs layout re-runs once the token arrives and completes the save.
  if (!getAccessToken()) {
    return "unavailable";
  }

  let publicKey: string | null = null;
  try {
    ({ publicKey } = await usersApi.webPushPublicKey());
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      console.warn(
        "Web Push is not available on this API build yet. Rebuild/restart the API to enable browser push notifications.",
      );
      return "unavailable";
    }
    throw err;
  }

  if (!publicKey) return "unavailable";

  await navigator.serviceWorker.register("/opncue-push-sw.js");
  const registration = await navigator.serviceWorker.ready;
  const applicationServerKey = urlBase64ToUint8Array(publicKey);

  let subscription = await registration.pushManager.getSubscription();

  // Drop any subscription tied to a stale VAPID key so we can re-subscribe with
  // the server's current public key (otherwise pushes silently fail with 403).
  if (
    subscription &&
    !subscriptionMatchesKey(subscription, applicationServerKey)
  ) {
    try {
      await subscription.unsubscribe();
    } catch {
      // Best effort: even if unsubscribe fails, subscribe() below replaces it.
    }
    subscription = null;
  }

  subscription ??= await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey,
  });

  const json = subscription.toJSON();

  if (!isWebPushSubscriptionPayload(json)) {
    console.warn(
      "[push] the browser returned a subscription in an unexpected shape; nothing was registered.",
    );
    return "unavailable";
  }

  try {
    await usersApi.setWebPushSubscription(json);
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      console.warn(
        "Web Push subscription endpoint is not available on this API build yet. Rebuild/restart the API to enable browser push notifications.",
      );
      return "unavailable";
    }
    // 400 = the API refused to store this subscription. Whatever the reason
    // (endpoint allowlist, malformed keys, a field it will not accept),
    // sending the same payload again cannot change the answer — so surface it
    // rather than letting the caller report success.
    if (err instanceof ApiError && err.status === 400) {
      throw new PushRegistrationRejectedError(err.message);
    }
    throw err;
  }

  return "stored";
}

function configureNotificationHandler(Notifications: NotificationsModule) {
  if (notificationHandlerConfigured) return;

  // Show alerts/sounds while the app is foregrounded — unless the thing being
  // announced is already over. That happens for pushes sent before delivery
  // deadlines shipped (four weeks of them may still be queued at FCM) and for
  // a Cue answered or withdrawn inside its own window.
  //
  // Foreground only, and the limit is worth stating: a notification delivered
  // while the app is backgrounded is drawn by the OS without asking this
  // handler at all. `reconcileCueNotifications` is what clears those.
  Notifications.setNotificationHandler({
    handleNotification: async (notification) => {
      const data = notification.request?.content?.data as
        | Record<string, unknown>
        | undefined;
      if (notificationExpired(data)) {
        return {
          shouldShowBanner: false,
          shouldShowList: false,
          shouldPlaySound: false,
          shouldSetBadge: false,
        };
      }
      return {
        shouldShowBanner: true,
        shouldShowList: true,
        shouldPlaySound: true,
        shouldSetBadge: false,
      };
    },
  });
  notificationHandlerConfigured = true;
}

async function ensureAndroidDefaultChannel(
  Notifications: NotificationsModule,
): Promise<void> {
  if (Platform.OS !== "android" || androidDefaultChannelReady) return;

  await Notifications.setNotificationChannelAsync("default", {
    name: "Opncue",
    importance: Notifications.AndroidImportance.MAX,
    // Do NOT set `sound` here. In expo-notifications the channel `sound` is a
    // *custom* bundled filename (via the config plugin's `sounds` array), not
    // the literal "default" — passing "default" throws "custom sound not found"
    // and aborts channel creation. Omitting `sound` uses the system default.
    enableVibrate: true,
    vibrationPattern: [0, 250, 250, 250],
    lightColor: "#78AEFF",
  });
  androidDefaultChannelReady = true;
}

function resolveProjectId(): string | undefined {
  const candidates = [
    process.env.EXPO_PUBLIC_EAS_PROJECT_ID,
    process.env.EXPO_PROJECT_ID,
    Constants.expoConfig?.extra?.eas?.projectId,
    Constants.easConfig?.projectId,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim().length > 0) {
      return candidate.trim();
    }
  }

  return undefined;
}

async function uploadNativePushToken(token: string): Promise<boolean> {
  const accessToken = getAccessToken();
  if (!accessToken) {
    console.warn(
      "Expo push token was created but not uploaded because the session token is not ready.",
    );
    return false;
  }

  const platform = Platform.OS === "ios" ? "ios" : "android";
  const uploadKey = `${platform}:${token}:${accessToken.slice(-16)}`;
  if (uploadKey === lastUploadedNativeTokenKey) {
    console.info(`[push] ${platform} push token already registered for this session.`);
    return true;
  }

  await usersApi.setPushToken(token, platform);
  lastUploadedNativeTokenKey = uploadKey;
  console.info(`Registered ${platform} push token with the API.`);
  return true;
}

async function registerNativePushNotifications(
  requestPermission: boolean,
): Promise<PushSetupOutcome> {
  const Notifications = await getNotificationsModule();
  if (!Notifications) {
    // In a real (non-Expo-Go) build this means the expo-notifications native
    // module failed to load — usually a stale build that predates it.
    console.warn(
      `[push] expo-notifications module unavailable (isExpoGo=${isRunningInExpoGo()}, ` +
        `appOwnership=${Constants.appOwnership}). If this is a dev/standalone build, ` +
        "rebuild the app so the native module is included.",
    );
    return "denied";
  }

  await ensureAndroidDefaultChannel(Notifications);

  const existing = await Notifications.getPermissionsAsync();
  let status = existing.status;
  console.info(`[push] permission before request: ${status}`);

  if (status !== "granted") {
    if (!requestPermission) {
      console.info("[push] permission not granted; passive sync skipped.");
      return "denied";
    }

    const requested = await Notifications.requestPermissionsAsync();
    status = requested.status;
  }

  console.info(`[push] permission after request: ${status}`);
  if (status !== "granted") return "denied";

  // Permission is settled by this point, and the token step below cannot
  // change it. See syncExpoPushToken.
  await syncExpoPushToken(Notifications);

  // Native reports only these two, exactly as it always has — see the note on
  // `PushSetupOutcome`. The token upload is best-effort with a foreground
  // retry, and making its failure visible here is a native behaviour change
  // that belongs to a native task, not to the web-push work.
  return "registered";
}

/**
 * Mint this device's Expo push token and hand it to the API.
 *
 * Deliberately returns void. Permission and token delivery are different
 * questions: this reaches the network (exp.host) and needs a live API session,
 * so it fails on a device whose user has perfectly well granted permission —
 * offline, flaky DNS, a build without FCM wired in, or a session token that is
 * not ready yet. Reporting those as "denied" made the very first Allow look
 * like a refusal and pushed the caller into a pointless "try again" / open-
 * settings loop.
 *
 * Failures are logged and left alone. The passive sync on foreground
 * (`registerForPushNotifications({ requestPermission: false })`, wired up in
 * the tabs layout) retries whenever the app comes back, so the token lands as
 * soon as the network does.
 */
async function syncExpoPushToken(
  Notifications: NotificationsModule,
): Promise<void> {
  try {
    const projectId = resolveProjectId();
    if (!projectId) {
      console.warn(
        "[push] no EAS project id configured — cannot mint an Expo push token.",
      );
      return;
    }
    console.info(`[push] requesting Expo push token (projectId=${projectId})`);

    const { data: token } = await Notifications.getExpoPushTokenAsync({
      projectId,
    });

    if (!token) {
      console.warn("[push] Expo did not return a native push token.");
      return;
    }

    console.info(`[push] got Expo token ${token.slice(0, 24)}… — uploading`);
    await uploadNativePushToken(token);
  } catch (err) {
    // Usual causes: no network (exp.host unreachable), or FCM not wired into
    // the *installed* build (google-services.json / plugin missing, stale APK).
    console.warn(
      `[push] failed to obtain/upload Expo push token: ${(err as Error)?.message ?? err} — ` +
        "notification permission is unaffected; retrying on next foreground.",
    );
  }
}

/**
 * Ask for notification permission and, if granted, register this device's Expo
 * push token with the backend so it can receive cue notifications.
 * Safe to call anywhere: it degrades gracefully on web / when a push token
 * cannot be obtained (e.g. no EAS project id).
 */
export async function registerForPushNotifications(
  options: RegisterPushOptions = {},
): Promise<PushSetupOutcome> {
  const requestPermission = options.requestPermission ?? true;

  if (Platform.OS === "web") {
    const permission = await requestBrowserNotificationPermission();
    if (permission !== "granted") return "denied";
    try {
      return (await registerBrowserPushSubscription()) === "stored"
        ? "registered"
        : "unavailable";
    } catch (err) {
      if (err instanceof PushRegistrationRejectedError) {
        // Not routed to device settings by any caller: permission is granted,
        // so there is nothing there for the user to change.
        console.warn(
          `[push] the API rejected this browser's push subscription; browser notifications will not arrive. ${err.detail ?? ""}`.trim(),
        );
        return "rejected";
      }
      // Offline, a server blip, anything else: probably temporary, and never a
      // claim that delivery works.
      console.warn("Unable to register browser push notifications.", err);
      return "unavailable";
    }
  }

  if (nativePushRegistrationInFlight) {
    const current = nativePushRegistrationInFlight;
    if (current.requestPermission || !requestPermission) {
      console.info("[push] registration already in progress — joining it.");
      return current.promise;
    }

    await current.promise.catch(() => "denied");
  }

  const promise = registerNativePushNotifications(requestPermission).finally(() => {
    if (nativePushRegistrationInFlight?.promise === promise) {
      nativePushRegistrationInFlight = null;
    }
  });
  nativePushRegistrationInFlight = { requestPermission, promise };
  return promise;
}

/**
 * Read the current notification permission WITHOUT prompting.
 *
 * Two uses: re-checking after the user has been sent to OS settings (the app is
 * not told when they change it there — you have to look again on foreground),
 * and deciding whether asking is even possible before showing an "allow"
 * button that the OS would ignore.
 */
export async function getNotificationPermission(): Promise<NotificationPermissionState> {
  if (Platform.OS === "web") {
    if (typeof window === "undefined" || !("Notification" in window)) {
      return { status: "denied", canAskAgain: false };
    }

    const permission = globalThis.Notification.permission;
    return {
      status: permission === "granted" ? "granted" : "denied",
      // "default" is the browser's undetermined state — the only one where a
      // request still opens a prompt.
      canAskAgain: permission === "default",
    };
  }

  const Notifications = await getNotificationsModule();
  if (!Notifications) return { status: "denied", canAskAgain: false };

  const { status, canAskAgain } = await Notifications.getPermissionsAsync();
  return {
    status: status === "granted" ? "granted" : "denied",
    canAskAgain,
  };
}

export function showBrowserNotification(
  title: string,
  payload: LocalNotificationPayload = {},
): void {
  if (
    Platform.OS !== "web" ||
    typeof window === "undefined" ||
    !("Notification" in window) ||
    globalThis.Notification.permission !== "granted"
  ) {
    return;
  }

  try {
    const notification = new globalThis.Notification(title, {
      body: payload.body,
      tag:
        typeof payload.data?.type === "string"
          ? `opncue-${payload.data.type}`
          : "opncue",
    });
    notification.onclick = () => {
      window.focus();
      notification.close();
    };
  } catch {
    // Browser notifications can still be blocked by context/browser policy.
  }
}

/**
 * Whether a notification of the given category is allowed by the user's
 * preferences. Everything is opt-out: missing preferences default to enabled.
 */
export function notificationAllowed(
  preferences: NotificationPreferences | null | undefined,
  category?: NotificationCategory,
): boolean {
  if (preferences?.muteAll) return false;
  if (!category) return true;
  return preferences?.[category] ?? true;
}

/**
 * Present a notification for an event the client itself observed, respecting
 * the user's preferences. Uses a *local* notification on native (the only kind
 * available in Expo Go) and the Web Notifications API on web.
 *
 * Remote push (server -> device) still flows through the backend for future
 * development/production builds, but local notifications are what make alerts
 * appear while the app is running in Expo Go.
 */
/**
 * Whether the app is actively in use right now: in the foreground on native, or
 * on a visible tab on web. Realtime alerts are suppressed in this state — the
 * in-app UI (unread badges, inbox) already reflects the update, so a sound/popup
 * would just be a distraction.
 */
export function isAppInForeground(): boolean {
  if (Platform.OS === "web") {
    return (
      typeof document !== "undefined" && document.visibilityState === "visible"
    );
  }
  return AppState.currentState === "active";
}

export async function presentLocalNotification(
  title: string,
  input: LocalNotificationInput = {},
  preferences?: NotificationPreferences | null,
): Promise<void> {
  if (!notificationAllowed(preferences, input.category)) return;

  // Stay quiet while the user is actively using the app; only alert when it's
  // backgrounded (native) or the tab is hidden (web).
  if (isAppInForeground()) return;

  if (Platform.OS === "web") {
    showBrowserNotification(title, { body: input.body, data: input.data });
    return;
  }

  const Notifications = await getNotificationsModule();
  if (!Notifications) return;

  try {
    await ensureAndroidDefaultChannel(Notifications);
    await Notifications.scheduleNotificationAsync({
      content: {
        title,
        body: input.body,
        data: input.data ?? {},
      },
      trigger: null,
    });
  } catch {
    // Presenting can fail if permission was revoked; ignore silently.
  }
}

/**
 * Clear Cue notifications the Cue behind which is no longer waiting.
 *
 * The half of this problem no delivery deadline can reach. A push already
 * queued at FCM when the deadline shipped still carries four weeks; a Cue
 * accepted, declined or withdrawn ends long before its own `expiresAt`. Either
 * way the notification is sitting in the tray with nothing behind it, which is
 * exactly what was reported: a Cue announced, and an empty Cues tab.
 *
 * Called with the pending inbox the app has just read, so "still waiting" is
 * the server's answer rather than a guess. Anything else — messages, group
 * messages, the sender's own confirmation — is left alone: they are durable,
 * or they belong to a list this caller does not hold.
 */
export async function reconcileCueNotifications(
  pendingCueIds: readonly string[],
): Promise<void> {
  if (Platform.OS === "web") return;
  const Notifications = await getNotificationsModule();
  if (!Notifications) return;

  try {
    const presented = await Notifications.getPresentedNotificationsAsync();
    const waiting = new Set(pendingCueIds);
    await Promise.all(
      presented.map(async (notification) => {
        const data = notification.request?.content?.data as
          | Record<string, unknown>
          | undefined;
        const cueId = cueIdOf(data);
        if (!cueId || waiting.has(cueId)) return;
        await Notifications.dismissNotificationAsync(
          notification.request.identifier,
        );
      }),
    );
  } catch {
    // Reading or dismissing can fail if permission was revoked. A tray we
    // could not tidy is not worth surfacing.
  }
}

/**
 * Resolve the in-app route to open for a notification's data payload. Prefers
 * an explicit `url`, otherwise derives one from the notification `type` (and
 * `chatId` when present). Returns `null` when there is nothing to open.
 */
function routeFromNotificationData(
  data: Record<string, unknown> | undefined | null,
): string | null {
  if (!data) return null;

  if (typeof data.url === "string" && data.url.length > 0) {
    return data.url;
  }

  // Tapping a notification whose subject has finished must not open the
  // subject. It lands on the list the subject would have been in, which is a
  // screen that can explain itself — an empty Cues tab reads as "nothing
  // waiting", where a dead entity screen reads as broken.
  const finished = notificationExpired(data);
  const chatId = !finished && typeof data.chatId === "string" ? data.chatId : null;
  const groupId = !finished && typeof data.groupId === "string" ? data.groupId : null;

  switch (data.type) {
    case "group_message":
      return groupId ? `/group/${groupId}` : "/(tabs)/inbox";
    case "message":
    case "cue_accepted":
      return chatId ? `/chat/${chatId}` : "/(tabs)/inbox";
    case "cue":
    case "cue_sent":
      return "/(tabs)/cues";
    case "new_person":
      return "/(tabs)/home";
    case "reflection_reminder":
    case "goal_reminder":
      return "/(tabs)/profile";
    default:
      return null;
  }
}

/**
 * Stable identifier for the one-shot "evenings" reminder (re-armed each day).
 * The repeating weekly/custom reminders use per-weekday ids instead.
 */
const REFLECTION_REMINDER_ID = "opncue-reflection-reminder";

/** expo-notifications weekday values (1 = Sunday … 7 = Saturday). */
const EXPO_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

/** Per-weekday identifier for a repeating weekly reflection reminder. */
function weeklyReminderId(expoWeekday: number): string {
  return `${REFLECTION_REMINDER_ID}-w${expoWeekday}`;
}

/** The content shown by every reflection reminder (one-shot or repeating). */
function reflectionReminderContent() {
  return {
    title: i18n.t("growth.reminders.reflectionTitle"),
    body: i18n.t("growth.reminders.reflectionBody"),
    data: { type: "reflection_reminder", url: "/(tabs)/profile" },
  };
}

/** Cancel every pending repeating weekly reminder (all seven weekday slots). */
async function cancelWeeklyReflectionReminders(
  Notifications: NotificationsModule,
): Promise<void> {
  await Promise.all(
    EXPO_WEEKDAYS.map((weekday) =>
      Notifications.cancelScheduledNotificationAsync(
        weeklyReminderId(weekday),
      ).catch(() => {}),
    ),
  );
}

/**
 * Schedule (replacing any existing) the one-shot "evenings" reflection reminder
 * to fire at `when`. Native only, and only if notification permission is already
 * granted — the caller (src/lib/reflection-reminder.ts) owns the "should we?"
 * decision. Also clears any repeating weekly reminders so cadences never stack.
 */
export async function scheduleReflectionReminder(when: Date): Promise<void> {
  if (Platform.OS === "web") return;

  const Notifications = await getNotificationsModule();
  if (!Notifications) return;

  try {
    const perms = await Notifications.getPermissionsAsync();
    if (perms.status !== "granted") return;

    await cancelWeeklyReflectionReminders(Notifications);

    // Scheduling with the same identifier replaces the pending one.
    await Notifications.scheduleNotificationAsync({
      identifier: REFLECTION_REMINDER_ID,
      content: reflectionReminderContent(),
      trigger: {
        type: Notifications.SchedulableTriggerInputTypes.DATE,
        date: when,
      },
    });
  } catch {
    // Scheduling can fail if permission was revoked mid-flight; ignore.
  }
}

/**
 * Schedule repeating weekly reflection reminders for the given `slots` (each an
 * expo weekday 1–7 plus hour/minute), replacing every existing reflection
 * reminder first. Native only, permission-gated. Used by the "weekly" and
 * "custom" cadences — the OS fires these even on days the app isn't opened.
 */
export async function scheduleWeeklyReflectionReminders(
  slots: { weekday: number; hour: number; minute: number }[],
): Promise<void> {
  if (Platform.OS === "web") return;

  const Notifications = await getNotificationsModule();
  if (!Notifications) return;

  try {
    const perms = await Notifications.getPermissionsAsync();
    if (perms.status !== "granted") return;

    // Clear the one-shot and any stale weekday slots before re-scheduling.
    await Notifications.cancelScheduledNotificationAsync(
      REFLECTION_REMINDER_ID,
    ).catch(() => {});
    await cancelWeeklyReflectionReminders(Notifications);

    const content = reflectionReminderContent();
    await Promise.all(
      slots.map((slot) =>
        Notifications.scheduleNotificationAsync({
          identifier: weeklyReminderId(slot.weekday),
          content,
          trigger: {
            type: Notifications.SchedulableTriggerInputTypes.WEEKLY,
            weekday: slot.weekday,
            hour: slot.hour,
            minute: slot.minute,
          },
        }),
      ),
    );
  } catch {
    // Scheduling can fail if permission was revoked mid-flight; ignore.
  }
}

/** Cancel every pending reflection reminder (one-shot + repeating weekly). */
export async function cancelReflectionReminder(): Promise<void> {
  if (Platform.OS === "web") return;

  const Notifications = await getNotificationsModule();
  if (!Notifications) return;

  try {
    await Notifications.cancelScheduledNotificationAsync(
      REFLECTION_REMINDER_ID,
    ).catch(() => {});
    await cancelWeeklyReflectionReminders(Notifications);
  } catch {
    // Nothing scheduled (or permission issue) — ignore.
  }
}

/** Stable per-goal identifier so each goal has at most one pending reminder. */
function goalReminderId(goalId: string): string {
  return `opncue-goal-reminder-${goalId}`;
}

/**
 * Schedule (replacing any existing) a due-date reminder for one goal to fire at
 * `when`. Native only, and only if notification permission is already granted —
 * the caller (src/lib/goal-reminder.ts) owns the "should we?" decision.
 */
export async function scheduleGoalReminder(
  goalId: string,
  when: Date,
  body: string,
): Promise<void> {
  if (Platform.OS === "web") return;

  const Notifications = await getNotificationsModule();
  if (!Notifications) return;

  try {
    const perms = await Notifications.getPermissionsAsync();
    if (perms.status !== "granted") return;

    // Scheduling with the same identifier replaces the pending one.
    await Notifications.scheduleNotificationAsync({
      identifier: goalReminderId(goalId),
      content: {
        title: i18n.t("growth.reminders.goalTitle"),
        body,
        data: { type: "goal_reminder", url: "/(tabs)/profile" },
      },
      trigger: {
        type: Notifications.SchedulableTriggerInputTypes.DATE,
        date: when,
      },
    });
  } catch {
    // Scheduling can fail if permission was revoked mid-flight; ignore.
  }
}

/** Cancel any pending reminder for a goal. Safe to call anywhere. */
export async function cancelGoalReminder(goalId: string): Promise<void> {
  if (Platform.OS === "web") return;

  const Notifications = await getNotificationsModule();
  if (!Notifications) return;

  try {
    await Notifications.cancelScheduledNotificationAsync(
      goalReminderId(goalId),
    );
  } catch {
    // Nothing scheduled (or permission issue) — ignore.
  }
}

/**
 * Route to open when the app is launched by tapping a notification (cold
 * start). Returns `null` on web, when no notification launched the app, or when
 * the notification carries nothing to navigate to.
 */
export async function getInitialNotificationRoute(): Promise<string | null> {
  if (Platform.OS === "web") return null;

  const Notifications = await getNotificationsModule();
  if (!Notifications) return null;

  try {
    const response = await Notifications.getLastNotificationResponseAsync();
    return routeFromNotificationData(
      response?.notification.request.content.data as
        Record<string, unknown> | undefined,
    );
  } catch {
    return null;
  }
}

/**
 * Subscribe to notification taps while the app is running. Calls `handler`
 * with the in-app route to open. Resolves to an unsubscribe function.
 */
export async function subscribeToNotificationResponses(
  handler: (route: string) => void,
): Promise<() => void> {
  if (Platform.OS === "web") return () => {};

  const Notifications = await getNotificationsModule();
  if (!Notifications) return () => {};

  const subscription = Notifications.addNotificationResponseReceivedListener(
    (response) => {
      const route = routeFromNotificationData(
        response.notification.request.content.data as
          Record<string, unknown> | undefined,
      );
      if (route) handler(route);
    },
  );

  return () => subscription.remove();
}
